import { Scene, GameObjects } from 'phaser';
import { storage } from '../core/Storage';
import { audioManager } from '../core/CoreManagerAudio';
import { WeaponPanelRex } from '../ui/WeaponPanelRex';
import { SoundWidgetRex } from '../ui/SoundWidgetRex';
import { initLocalization, t } from '../i18n/Localization';

export class LevelSelect extends Scene {
  private scaleRatio: number = 1;
  private unlockedLevels: number[] = [1];
  private biomatter: number = 0;
  private weaponPanel: WeaponPanelRex | null = null;

  constructor() {
    super('LevelSelect');
  }

  async create(): Promise<void> {
    initLocalization(this);
    // Get unlocked levels and biomatter from storage
    this.unlockedLevels = await storage.getUnlockedLevels();
    this.biomatter = await storage.getBiomatter();

    // Play LevelSelect music
    audioManager.playMusicForScene('LevelSelect');

    this.createUI();
    this.createWeaponPanel();
  }

  private playButtonSound(type: 'hover' | 'click'): void {
    audioManager.playUISound(type, this);
  }

  private createUI(): void {
    const { width, height } = this.scale;
    this.scaleRatio = Math.min(width / 1600, height / 900);

    // Background - tiled dark pattern
    this.cameras.main.setBackgroundColor('#1a1a2e');
    
    // Fade in de entrada
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 500,
      ease: 'Power2'
    });

    // Top-right icons: Settings and Sound
    this.createTopRightIcons();

    // Title image
    const title = this.add.image(width / 2, 80 * this.scaleRatio, 'levelselect_title');
    title.setScale(this.scaleRatio * 0.8);

    // Biomatter display (below top-right icons)
    const bioY = 100; // Below the settings/sound icons
    const bioX = 1600 - 120; // Aligned with icons (same rightMargin)
    const bioIcon = this.add.image(bioX, bioY, 'hud_biomoney');
    bioIcon.setScale(1.5 * this.scaleRatio);
    bioIcon.setOrigin(1, 0.5);
    this.add.text(bioX + 10, bioY, `${this.biomatter}`, {
      fontFamily: 'Arial Black',
      fontSize: `${24 * this.scaleRatio}px`,
      color: '#44ff88',
    }).setOrigin(0, 0.5);

    // Level buttons grid (4 columns x 2 rows)
    const gridCols = 4;
    const buttonSpacingX = 180 * this.scaleRatio;
    const buttonSpacingY = 160 * this.scaleRatio;
    const gridStartX = width / 2 - (buttonSpacingX * (gridCols - 1)) / 2;
    const gridStartY = height * 0.35;

    for (let i = 1; i <= 8; i++) {
      const col = (i - 1) % gridCols;
      const row = Math.floor((i - 1) / gridCols);
      const x = gridStartX + col * buttonSpacingX;
      const y = gridStartY + row * buttonSpacingY;

      const isUnlocked = this.unlockedLevels.includes(i);
      // Stagger delay para cada botón
      const staggerDelay = 100 + i * 50;
      this.createLevelButton(x, y, i, isUnlocked, staggerDelay);
    }

    // Bottom row: Back button (left) and Workshop button (right)
    const bottomY = height - 80 * this.scaleRatio;
    const bottomMargin = 150 * this.scaleRatio;

    // Back button (icon + text style)
    this.createBackButton(bottomMargin, bottomY);

    // Workshop button (Rex UI style)
    this.createWorkshopButton(width - bottomMargin, bottomY);
  }

  private createTopRightIcons(): void {
    // Use fixed position from game's base resolution (1600x900)
    // Respect padding from screen edges (similar to bottom buttons)
    const iconY = 50; // 50px from top
    const iconScale = 0.5 * this.scaleRatio;
    const rightMargin = 120; // Match padding with rest of UI

    // Solo sonido aqui (configuracion queda en el MainMenu)
    new SoundWidgetRex({
      scene: this,
      x: 1600 - rightMargin,
      y: iconY,
      scale: iconScale,
      openDirection: 'left', // Open panel to the left since we're on right edge
      onMusicChange: (volume) => audioManager.setMusicVolume(volume),
      onSfxChange: (volume) => audioManager.setSfxVolume(volume),
    });
  }
  private createLevelButton(
    x: number,
    y: number,
    levelNum: number,
    unlocked: boolean,
    staggerDelay: number = 0
  ): GameObjects.Container {
    const container = this.add.container(x, y);
    const texturePrefix = `btn_level${levelNum}`;
    const btn = this.add.image(0, 0, `${texturePrefix}_off`);
    btn.setScale(this.scaleRatio * 0.9);
    container.add(btn);

    // Stagger animation: Fade in + pequeño scale
    container.setAlpha(0);
    container.setScale(0.8);
    this.tweens.add({
      targets: container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      delay: staggerDelay,
      ease: 'Back.easeOut'
    });

    if (!unlocked) {
      // Dim the button
      btn.setAlpha(0.5);
      btn.setTint(0x888888);

      // Add lock icon overlay
      const lock = this.add.image(0, 0, 'icon_lock');
      lock.setScale(this.scaleRatio * 0.6);
      container.add(lock);

      return container;
    }
    
    // Pulse suave infinito para botones desbloqueados
    this.tweens.add({
      targets: container,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 1500,
      delay: staggerDelay + 400,
      yoyo: true,
      loop: -1,
      ease: 'Sine.easeInOut'
    });

    btn.setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => {
      btn.setTexture(`${texturePrefix}_on`);
      this.playButtonSound('hover');
    });

    btn.on('pointerout', () => {
      btn.setTexture(`${texturePrefix}_off`);
    });

    btn.on('pointerdown', () => {
      btn.setTexture(`${texturePrefix}_selected`);
      this.playButtonSound('click');
    });

    btn.on('pointerup', () => {
      btn.setTexture(`${texturePrefix}_on`);
      this.selectLevel(levelNum);
    });

    return container;
  }

  private createSpriteButton(
    x: number,
    y: number,
    texturePrefix: string,
    scale: number,
    onClick: () => void
  ): GameObjects.Image {
    const btn = this.add.image(x, y, `${texturePrefix}_off`);
    btn.setScale(scale);
    btn.setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => {
      btn.setTexture(`${texturePrefix}_on`);
      this.playButtonSound('hover');
    });

    btn.on('pointerout', () => {
      btn.setTexture(`${texturePrefix}_off`);
    });

    btn.on('pointerdown', () => {
      btn.setTexture(`${texturePrefix}_selected`);
      this.playButtonSound('click');
    });

    btn.on('pointerup', () => {
      btn.setTexture(`${texturePrefix}_on`);
      onClick();
    });

    return btn;
  }

  private selectLevel(levelNum: number): void {
    // Go to LevelPreloader to load level-specific assets
    this.scene.start('LevelPreloader', {
      levelId: `level${levelNum}`,
      levelNum: levelNum,
      weapons: {
        primary: 'laserSimple',
        secondary: null,
        special: 'tractorPuny',
      },
    });
  }

  private createWeaponPanel(): void {
    const { width } = this.scale;
    const panelWidth = Math.min(400 * this.scaleRatio, width * 0.4);

    this.weaponPanel = new WeaponPanelRex({
      scene: this,
      width: panelWidth,
      onStart: (levelNum, weapons) => {
        // Go to LevelPreloader to load level-specific assets
        this.scene.start('LevelPreloader', {
          levelId: `level${levelNum}`,
          levelNum: levelNum,
          weapons: {
            primary: weapons.find(w => w.id === 'primary')?.equipped,
            secondary: weapons.find(w => w.id === 'secondary')?.equipped,
            special: weapons.find(w => w.id === 'special')?.equipped,
          },
        });
      },
      onClose: () => {
        // Panel closed, nothing special to do
      },
    });
  }

  private openWorkshop(): void {
    // Go to WeaponWorkshop scene
    this.scene.start('WeaponWorkshop', { level: 1 });
  }

  private createBackButton(x: number, y: number): void {
    const btnWidth = 160 * this.scaleRatio;
    const btnHeight = 55 * this.scaleRatio;
    const iconSize = 28 * this.scaleRatio;

    const backBtn = this.rexUI.add.label({
      x: x,
      y: y,
      width: btnWidth,
      height: btnHeight,
      background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 10, 0x2d2d44)
        .setStrokeStyle(3, 0x4a9c6d),
      icon: this.add.image(0, 0, 'icon_arrow_left').setDisplaySize(iconSize, iconSize),
      text: this.add.text(0, 0, t('ui.back'), {
        fontFamily: 'Crackvetica, Arial Black',
        fontSize: `${22 * this.scaleRatio}px`,
        color: '#ffffff',
      }),
      space: { left: 15, right: 20, top: 10, bottom: 10, icon: 8 },
      align: 'center'
    });

    backBtn.setInteractive({ useHandCursor: true });

    backBtn.on('pointerover', () => {
      const bg = backBtn.getElement('background') as any;
      if (bg) {
        bg.setFillStyle(0x3d3d54);
        bg.setStrokeStyle(3, 0x5cbf60);
      }
      backBtn.setScale(1.05);
      this.playButtonSound('hover');
    });

    backBtn.on('pointerout', () => {
      const bg = backBtn.getElement('background') as any;
      if (bg) {
        bg.setFillStyle(0x2d2d44);
        bg.setStrokeStyle(3, 0x4a9c6d);
      }
      backBtn.setScale(1);
    });

    backBtn.on('pointerdown', () => {
      const bg = backBtn.getElement('background') as any;
      if (bg) bg.setFillStyle(0x1d1d34);
      this.playButtonSound('click');
    });

    backBtn.on('pointerup', () => {
      const bg = backBtn.getElement('background') as any;
      if (bg) bg.setFillStyle(0x3d3d54);
      this.scene.start('MainMenu');
    });

    backBtn.layout();
  }

  private createWorkshopButton(x: number, y: number): void {
    const btnWidth = 180 * this.scaleRatio;
    const btnHeight = 55 * this.scaleRatio;

    const workshopBtn = this.rexUI.add.label({
      x: x,
      y: y,
      width: btnWidth,
      height: btnHeight,
      background: this.rexUI.add.roundRectangle(0, 0, 0, 0, 10, 0x2d2d44)
        .setStrokeStyle(3, 0x4a9c6d),
      text: this.add.text(0, 0, t('ui.loadout'), {
        fontFamily: 'Crackvetica, Arial Black',
        fontSize: `${22 * this.scaleRatio}px`,
        color: '#ffffff',
      }),
      space: { left: 15, right: 15, top: 10, bottom: 10 },
      align: 'center'
    });

    workshopBtn.setInteractive({ useHandCursor: true });

    workshopBtn.on('pointerover', () => {
      const bg = workshopBtn.getElement('background') as any;
      if (bg) {
        bg.setFillStyle(0x3d3d54);
        bg.setStrokeStyle(3, 0x5cbf60);
      }
      workshopBtn.setScale(1.05);
      this.playButtonSound('hover');
    });

    workshopBtn.on('pointerout', () => {
      const bg = workshopBtn.getElement('background') as any;
      if (bg) {
        bg.setFillStyle(0x2d2d44);
        bg.setStrokeStyle(3, 0x4a9c6d);
      }
      workshopBtn.setScale(1);
    });

    workshopBtn.on('pointerdown', () => {
      const bg = workshopBtn.getElement('background') as any;
      if (bg) bg.setFillStyle(0x1d1d34);
      this.playButtonSound('click');
    });

    workshopBtn.on('pointerup', () => {
      const bg = workshopBtn.getElement('background') as any;
      if (bg) bg.setFillStyle(0x3d3d54);
      this.openWorkshop();
    });

    workshopBtn.layout();
  }
}

